import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const dd = d.getDate().toString().padStart(2, "0");
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = d.getHours().toString().padStart(2, "0");
  const min = d.getMinutes().toString().padStart(2, "0");
  return `${dd}.${mm}.${yyyy} kl. ${hh}:${min}`;
}

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mailSent, setMailSent] = useState(false);
  const [mailError, setMailError] = useState("");

  // 1) Hent ordre
  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/orders/${id}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error("Kunne ikke hente ordre");
        const data = await res.json();
        setOrder(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          setOrder(null);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [id]);

  // 2) Send e-post (idempotent) når ordren er lastet inn
  useEffect(() => {
    if (!order?.id) return;
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/orders/${order.id}/send-confirmation`,
          { method: "POST", signal: controller.signal }
        );
        // idempotent endpoint: 200/201/204 etc regnes som OK
        if (res.ok) {
          setMailSent(true);
          setMailError("");
        } else {
          const body = await res.json().catch(() => ({}));
          setMailSent(false);
          setMailError(body?.error || "Klarte ikke å sende ordrebekreftelse.");
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setMailSent(false);
          setMailError("Klarte ikke å sende ordrebekreftelse (nettverksfeil).");
        }
      }
    })();

    return () => controller.abort();
  }, [order?.id]);

  if (loading) {
    return <div className="text-center mt-16">Laster kvittering ...</div>;
  }

  if (!order) {
    return (
      <div className="text-center mt-16 text-red-600">
        Fant ikke ordre.
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-16 text-center">
      <h1 className="text-2xl font-bold mb-6 text-green-700">
        Takk for din bestilling!
      </h1>

      <div className="mb-4 text-gray-700">
        Ordre-ID: <b>{order.id}</b>
      </div>

      {/* E-poststatus (valgfritt å vise) */}
      {mailError ? (
        <div className="mb-6 px-3 py-2 rounded border border-red-300 bg-red-50 text-red-700 text-sm">
          {mailError}
        </div>
      ) : mailSent ? (
        <div className="mb-6 px-3 py-2 rounded border border-green-300 bg-green-50 text-green-700 text-sm">
          Ordrebekreftelse er sendt på e-post.
        </div>
      ) : null}

      <ul className="divide-y divide-gray-200 text-left mb-8">
        {order.OrderItems?.map((item) => (
          <li key={item.id} className="py-3">
            <div className="font-medium">{item.name}</div>
            {item.date_from || item.date_to ? (
              <div className="text-sm">
                {formatDate(item.date_from)} &rarr; {formatDate(item.date_to)}
              </div>
            ) : (
              <div className="text-sm text-gray-500">Kjøp</div>
            )}
            <div className="text-sm">Antall: {item.quantity}</div>
            <div className="font-bold">{Number(item.price || 0)} kr</div>
          </li>
        ))}
      </ul>

      <div className="font-bold text-lg mb-8">
        Totalpris: {Number(order.total || 0)} kr
      </div>

      <Link
        to="/"
        className="bg-blue-700 text-white px-6 py-2 rounded hover:bg-blue-900"
      >
        Tilbake til forsiden
      </Link>
    </div>
  );
}
